import React, { useState, useRef, useEffect } from "react";
import { Box, useMediaQuery, IconButton } from "@mui/material";
import { ArrowBackIosOutlined, ArrowForwardIosOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const ProductGallery = ({ productImages = [], fallbackImage }) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const images = productImages.length > 0 ? productImages : (fallbackImage ? [fallbackImage] : []);
  const [activeIndex, setActiveIndex] = useState(0);
  const [showDock, setShowDock] = useState(true);
  const [touchStart, setTouchStart] = useState(null);
  const thumbnailsRef = useRef(null);

  useEffect(() => {
    setShowDock(true);
    const timer = setTimeout(() => setShowDock(false), 1500);
    return () => clearTimeout(timer);
  }, [activeIndex]);

  useEffect(() => {
    if (thumbnailsRef.current) {
      const container = thumbnailsRef.current;
      const activeThumb = container.children[activeIndex];
      if (activeThumb) {
        const containerCenter = container.offsetWidth / 2;
        const thumbCenter = activeThumb.offsetLeft + activeThumb.offsetWidth / 2;
        container.scrollTo({
          left: thumbCenter - containerCenter,
          behavior: "smooth"
        });
      }
    }
  }, [activeIndex]);

  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const BackButton = () => (
    <IconButton
      onClick={handleBack}
      sx={{
        position: "absolute",
        top: { xs: 16, md: 24 },
        left: { xs: 16, md: 24 },
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(4px)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        zIndex: 20,
        "&:hover": {
          backgroundColor: "#fff",
          transform: "scale(1.05)",
        },
        transition: "all 0.2s ease",
      }}
    >
      <ArrowBackIosOutlined sx={{ fontSize: "18px", color: "#1a1a1a", ml: "4px" }} />
    </IconButton>
  );

  const handleNext = () => {
    setActiveIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrev = () => {
    setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (touchStart === null) return;
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;
    if (diff > 50) handleNext();
    if (diff < -50) handlePrev();
    setTouchStart(null);
  };

  // Desktop View: Huge Main Image with Glass Border & Arrows
  return (
    <Box sx={{
      width: "100%",
      position: "relative",
      display: "flex",
      flexDirection: "column",
      p: 0,
      pt: { xs: 0, md: 1 }
    }}>

      {/* Opaque Glass Border Wrapper */}
      <Box sx={{
        width: "100%",
        aspectRatio: { xs: "1333 / 1750", md: "1350 / 1700" },
        borderRadius: { xs: 0, md: "32px" },
        padding: { xs: 0, md: "12px" },
        background: { xs: "transparent", md: "rgba(235, 235, 235, 0.6)" },
        backdropFilter: { xs: "none", md: "blur(24px)" },
        boxShadow: { xs: "none", md: "0 24px 48px rgba(0,0,0,0.08), inset 0 1px 1px rgba(255,255,255,0.8)" },
        border: { xs: "none", md: "1px solid rgba(0,0,0,0.04)" },
        position: "relative",
      }}>

        <BackButton />

        {/* Main Image Container */}
        <Box
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          sx={{
            width: "100%",
            height: "100%",
            position: "relative",
            borderRadius: { xs: 0, md: "20px" },
            overflow: "hidden",
            backgroundColor: "#FFFFFF",
            boxShadow: { xs: "none", md: "inset 0 0 0 1px rgba(0,0,0,0.05)" }
          }}>

          {images.map((img, idx) => (
            <Box
              key={idx}
              component="img"
              src={img}
              alt={`Product Image ${idx + 1}`}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = require("../../assets/images/no_image.png");
              }}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                opacity: activeIndex === idx ? 1 : 0,
                transition: "opacity 0.6s ease-in-out, transform 0.6s ease-in-out",
                transform: activeIndex === idx ? "scale(1)" : "scale(1.03)",
                pointerEvents: activeIndex === idx ? "auto" : "none",
              }}
            />
          ))}

          {/* Subtle glass overlay gradient at bottom */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              height: "25%",
              background: "linear-gradient(to top, rgba(0,0,0,0.1) 0%, transparent 100%)",
              pointerEvents: "none",
            }}
          />

          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <IconButton
                onClick={handlePrev}
                sx={{
                  display: { xs: "none", md: "inline-flex" },
                  position: "absolute",
                  left: 24,
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(255, 255, 255, 0.4)",
                  backdropFilter: "blur(16px)",
                  border: "1px solid rgba(255, 255, 255, 0.6)",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
                  zIndex: 20,
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    border: "1px solid rgba(255, 255, 255, 0.9)",
                    transform: "translateY(-50%) scale(1.1)",
                  },
                  transition: "all 0.3s ease",
                  width: "48px",
                  height: "48px",
                }}
              >
                <ArrowBackIosOutlined sx={{ fontSize: "20px", color: "#1a1a1a", ml: "4px" }} />
              </IconButton>

              <IconButton
                onClick={handleNext}
                sx={{
                  display: { xs: "none", md: "inline-flex" },
                  position: "absolute",
                  right: 24,
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(255, 255, 255, 0.4)",
                  backdropFilter: "blur(16px)",
                  border: "1px solid rgba(255, 255, 255, 0.6)",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
                  zIndex: 20,
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    border: "1px solid rgba(255, 255, 255, 0.9)",
                    transform: "translateY(-50%) scale(1.1)",
                  },
                  transition: "all 0.3s ease",
                  width: "48px",
                  height: "48px",
                }}
              >
                <ArrowForwardIosOutlined sx={{ fontSize: "20px", color: "#1a1a1a" }} />
              </IconButton>
            </>
          )}

          {/* Horizontal Thumbnails Hover Zone */}
          {images.length > 1 && (
            <Box sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "120px",
              zIndex: 20,
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-end",
              pb: "24px",
              "&:hover .thumbnail-dock": {
                opacity: 1,
                transform: "translateY(0)",
                pointerEvents: "auto",
              }
            }}>
              <Box
                className="thumbnail-dock"
                sx={{
                  opacity: showDock ? 1 : 0,
                  transform: showDock ? "translateY(0)" : "translateY(10px)",
                  pointerEvents: showDock ? "auto" : "none",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: 1.5,
                  px: 2,
                  py: 1.5,
                  maxWidth: "244px",
                  overflowX: "auto",
                  scrollBehavior: "smooth",
                  "&::-webkit-scrollbar": { display: "none" },
                  scrollbarWidth: "none",
                  backgroundColor: "rgba(255, 255, 255, 0.4)",
                  backdropFilter: "blur(16px)",
                  borderRadius: "16px",
                  border: "1px solid rgba(255, 255, 255, 0.6)",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
                }}
                ref={thumbnailsRef}
              >
                {images.map((img, idx) => (
                  <Box
                    key={idx}
                    onClick={() => setActiveIndex(idx)}
                    sx={{
                      flexShrink: 0,
                      width: "44px",
                      aspectRatio: "1333 / 2000",
                      borderRadius: "6px",
                      overflow: "hidden",
                      cursor: "pointer",
                      opacity: activeIndex === idx ? 1 : 0.6,
                      transform: activeIndex === idx ? "scale(1.08)" : "scale(1)",
                      transition: "all 0.3s ease",
                      border: activeIndex === idx ? "2px solid #fff" : "1px solid rgba(255,255,255,0.4)",
                      boxShadow: activeIndex === idx ? "0 4px 12px rgba(0,0,0,0.3)" : "none",
                    }}
                  >
                    <Box
                      component="img"
                      src={img}
                      alt={`Thumbnail ${idx + 1}`}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = require("../../assets/images/no_image.png");
                      }}
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}

        </Box>
      </Box>
    </Box>
  );
};

export default ProductGallery;
